import { useSyncExternalStore, type ReactElement, type ReactNode } from 'react';

import { I18nContext, type I18nContextValue } from '@/lib/i18n/catalog-context.js';
import { catalogSnapshot, subscribeCatalog } from '@/lib/i18n/catalog-store.js';

/**
 * Провайдер каталога локали.
 *
 * ⚠️ Подписан на стор, а не получает готовое значение сверху. Причина — догрузка:
 * каталог больше не приезжает целиком до первого кадра, страница просит свой namespace
 * в ленивом импорте маршрута и пополняет стор. Без подписки дерево не узнало бы о
 * пополнении и продолжило рендерить по старому снимку.
 *
 * Провайдер по-прежнему НЕ загружает и НЕ привязывает каталог: и то и другое происходит
 * в lib/i18n/catalog-store.ts до первого рендера. Причина в main.tsx: загрузка обёрнута
 * в try/catch, который на сбое показывает <BootFailure/>. Если бы привязка шла здесь,
 * внутри дерева React, тот же сбой случился бы ПОСЛЕ первого рендера и в этот catch
 * не попал бы — вместо аварийного экрана пользователь увидел бы белую страницу.
 *
 * ⚠️ `value` остаётся пропом ради компонентных тестов: они подставляют подставной каталог
 * (test/render-with-providers.tsx) и не поднимают ни сети, ни стора. В приложении проп
 * не передаётся никогда.
 */
export function CatalogProvider({
  value,
  children,
}: {
  value?: I18nContextValue;
  children: ReactNode;
}): ReactElement {
  const stored = useSyncExternalStore(subscribeCatalog, catalogSnapshot, catalogSnapshot);
  const resolved = value ?? stored;

  if (resolved === undefined) {
    // Недостижимо в приложении: main.tsx рендерит дерево только после bootCatalog().
    // Явная ошибка лучше, чем контекст со значением undefined, который выстрелит
    // где-то внизу в первом же useCatalog().
    throw new Error('CatalogProvider: catalog is not loaded and no value was provided');
  }

  return <I18nContext.Provider value={resolved}>{children}</I18nContext.Provider>;
}
